/**
 * VidyoDetection class
 *
 * This is a utility class that does browser interrogation
 * and detection of whether Vidyo components are installed
 * 1. Vidyo NPAPI plugin
 * 2. Vidyo Chrome Extensions (VidyoWebConnector, Vidyo Screen Share for WebRTC)
 * 3. Vidyo Chrome Native Messaging Host (VidyoClientForWeb, connected to by the VidyoWebConnector extension)
 */
namespace SmartVideo {
  interface CustomPluginType extends Plugin {
    type: string;
  }

  export class VidyoDetection implements IVidyoDetection {
    config: ISmartVideoConfig;
    pluginVersion: string;
    chromeExtensionUrl: string;
    extensionId: string;
    chromeNativeMessagingHostVersion: string;
    isInstalled: boolean;
    isChrome: boolean;
    isFireFox: boolean;
    isEdge: boolean;
    isOpera: boolean;
    isIE: boolean;
    isSafari: boolean;
    isSmartVideoAgent: boolean;
    isChromeExtensionInstalled: boolean;
    isChromeNativeMessagingHostInstalled: boolean;
    isBrowserSupported: boolean;
    isWebRTCCapable: boolean;
    isWebRTCAvailable: boolean;
    isCameraReady: boolean;
    isNeoSupported: boolean;
    isOsSupported: boolean;
    isMobile: boolean;
    isOSX: boolean;
    isIOS: boolean;
    isWindows: boolean;
    isInitializing: boolean;
    isInitialized: boolean;
    mobileOS: string;
    supportedOsName: string;
    installTimer: number;
    img: HTMLImageElement;
    // TODO: Need to create an interface defining browserReport for type safety
    browserReport: any;
    callbacks: Object;
    timeouts: Object;
    receiveListener: EventListener;
    useWebRTC: boolean;
    fireFoxCameraDetectionFunction: number;

    constructor() {
      this.img = new Image();
      this.isInstalled = false;
      this.isChrome = false;
      this.isFireFox = false;
      this.isEdge = false;
      this.isOpera = false;
      this.isIE = false;
      this.isIOS = false;
      this.isSmartVideoAgent = false;
      this.isChromeExtensionInstalled = false;
      this.isChromeNativeMessagingHostInstalled = false;
      this.isBrowserSupported = false;
      this.isWebRTCCapable = false;
      this.isWebRTCAvailable = false;
      this.isNeoSupported = false;
      this.isOsSupported = false;
      this.isOSX = false;
      this.isWindows = false;
      this.browserReport = this.extractDataFromClient();
      this.callbacks = new Object();
      this.timeouts = new Object();
      this.isMobile = this.isSupportedMobile();
      this.receiveListener = this.receiveVidyoAppEvent.bind(this);
      this.useWebRTC = false;
      this.isCameraReady = false;
      this.isInitialized = false;
      this.isInitializing = false;

      window.addEventListener('message', this.receiveListener, false);
    }
    // Need to rework init to just pass in config, since all the params currently passed are already in the config
    // init(mimePluginVersion: string, chromeNMSVersion: string, chromeExtensionUrl: string, useWebRTC: boolean, callback?: Function) {
    init(config: ISmartVideoConfig, callback?: Function) {
      this.config = config;
      const that = this;
      console.log('Initializing VidyoDetection');
      this.isInitializing = true;
      this.useWebRTC = config.UseWebRTC;
      this.browserSupported();

      /* If modern browser, hook change for media devices and do initial camera check.
            Exclude mobile devices, since we use VidyoMobile app */
      if (navigator.mediaDevices && !this.isIOS) {
        if (this.isChrome) {
          navigator.mediaDevices.ondevicechange = this.detectCamera.bind(this);
        }
        if (this.isFireFox) {
          /*
                    ondevicechange doesn't work under Firefox unless you first call getUserMedia
                    That's stupid, because you only want to call getUserMedia if you've detected a valid
                    device first.
                    So for Firefox, we just periodically poll the devices, which is a waste of CPU cycles
                    */
          this.fireFoxCameraDetectionFunction = setInterval(
            this.detectCamera.bind(this),
            500
          );
        }
        this.detectCamera();
      }

      if (config.VidyoWebVersion && config.VidyoWebVersion !== '') {
        this.pluginVersion = config.VidyoWebVersion;
      } else {
        this.pluginVersion = '1.3.10.0004';
      }
      if (
        config.VidyoClientForWebVersion &&
        config.VidyoClientForWebVersion !== ''
      ) {
        this.chromeNativeMessagingHostVersion = config.VidyoClientForWebVersion;
      } else {
        this.chromeNativeMessagingHostVersion = '1.3.10.0005';
      }
      if (config.UseWebRTC && this.isWebRTCCapable) {
        /*All WebRTC capable browsers support Promises, so safe to use it here*/
        console.log('UseWebRTC config set to true, and is WebRTCCapable');
        this.detectWebRTCAvailable().then(
          function (response) {
            that.chromeExtensionUrl =
              config.VidyoWebRTCScreenShareChromeExtensionUrl;
            that.isWebRTCAvailable = true;
            console.log(
              'Successfully contacted WebRTC Session Manager and recieved IP address to use for WebRTC'
            );
            that.finalizeInit();
          },
          function (error) {
            console.log(error.message);
            that.chromeExtensionUrl =
              config.VidyoClientForWebChromeExtensionUrl;
            that.finalizeInit();
          }
        );
      } else {
        this.finalizeInit();
      }
    }

    finalizeInit() {
      if (this.chromeExtensionUrl && this.chromeExtensionUrl !== '') {
        // this.chromeExtensionUrl = chromeExtensionUrl;
        // Get extensionId from chromeExtensionUrl
        this.extensionId = this.chromeExtensionUrl.split('/').slice(-1)[0];
        console.log('ExtensionId set as: ' + this.extensionId);
      }

      if (this.config.UseWebRTC) {
        if (!this.isWebRTCCapable || !this.isWebRTCAvailable) {
          this.useWebRTC = false;
        }
      } else {
        if (this.browserReport.os.name === 'Windows' && this.isOsSupported) {
          this.isNeoSupported = true;
        }
      }
      this.isInitialized = true;
      this.isInitializing = false;
    }

    isFunction(possibleFunction) {
      return typeof possibleFunction === typeof Function;
    }

    isSupportedMobile() {
      let isSupportedMobile = false;
      switch (this.browserReport.os.name) {
        case 'Android':
          isSupportedMobile = true;
          break;
        case 'iOS':
          isSupportedMobile = true;
          break;
        case 'OS X':
          if (this.browserReport.browser.name === 'Safari') {
            /* Treat Safari as mobile, for the purpose of showing the mobile modal so
            user can install the VidyoConnect package */
            isSupportedMobile = true;
          }
          break;
        default:
          break;
      }
      return isSupportedMobile;
    }

    doCallback(possiblecallback: Function) {
      if (this.isFunction(possiblecallback)) {
        possiblecallback();
      }
    }

    extractDataFromClient() {
      let report, match, uuid, definePropertySupported;
      const userAgent = navigator.userAgent;
      let isWin64;

      // initialize object to store results
      report = {
        browser: {
          name: null,
          version: null,
        },
        cookies: null,
        flash: {
          version: null,
        },
        ip: null,
        java: {
          version: null,
        },
        os: {
          name: null,
          version: null,
          cpu: null,
        },
        screen: {
          colors: null,
          dppx: null,
          height: null,
          width: null,
        },
        scripts: true,
        userAgent: userAgent,
        viewport: {
          height: null,
          layout: {
            height: null,
            width: null,
          },
          width: null,
          zoom: null,
        },
        websockets: null,
      };

      // extract cpuClass
      isWin64 =
        navigator.userAgent.indexOf('WOW64') > -1 ||
        navigator.userAgent.indexOf('Win64') > -1;
      if (isWin64) {
        report.os.cpu = 'Win64';
      } else {
        report.os.cpu = window.navigator.platform;
      }

      // extract browser name from user agent
      if (userAgent.indexOf('Trident') >= 0 || userAgent.indexOf('MSIE') >= 0) {
        if (userAgent.indexOf('Mobile') >= 0) {
          report.browser.name = 'IE Mobile';
        } else {
          report.browser.name = 'Internet Explorer';
        }
      }

      if (
        userAgent.indexOf('Firefox') >= 0 &&
        userAgent.indexOf('Seamonkey') === -1
      ) {
        if (userAgent.indexOf('Android') >= 0) {
          report.browser.name = 'Firefox for Android';
        } else {
          report.browser.name = 'Firefox';
        }
      }

      if (
        userAgent.indexOf('Safari') >= 0 &&
        userAgent.indexOf('Chrome') === -1 &&
        userAgent.indexOf('Chromium') === -1 &&
        userAgent.indexOf('Android') === -1
      ) {
        if (userAgent.indexOf('CriOS') >= 0) {
          report.browser.name = 'Chrome for iOS';
        } else if (userAgent.indexOf('FxiOS') >= 0) {
          report.browser.name = 'Firefox for iOS';
        } else {
          report.browser.name = 'Safari';
        }
      }

      if (userAgent.indexOf('Chrome') >= 0) {
        if (userAgent.match(/\bChrome\/[.0-9]* Mobile\b/)) {
          if (
            userAgent.match(/\bVersion\/\d+\.\d+\b/) ||
            userAgent.match(/\bwv\b/)
          ) {
            report.browser.name = 'WebView on Android';
          } else {
            report.browser.name = 'Chrome for Android';
          }
        } else {
          report.browser.name = 'Chrome';
        }
      }

      if (
        userAgent.indexOf('Android') >= 0 &&
        userAgent.indexOf('Chrome') === -1 &&
        userAgent.indexOf('Chromium') === -1 &&
        userAgent.indexOf('Trident') === -1 &&
        userAgent.indexOf('Firefox') === -1
      ) {
        report.browser.name = 'Android Browser';
      }

      if (userAgent.indexOf('Edge') >= 0) {
        report.browser.name = 'Edge';
      }

      if (userAgent.indexOf('UCBrowser') >= 0) {
        report.browser.name = 'UC Browser for Android';
      }

      if (userAgent.indexOf('OPR') >= 0 || userAgent.indexOf('Opera') >= 0) {
        if (userAgent.indexOf('Opera Mini') >= 0) {
          report.browser.name = 'Opera Mini';
        } else {
          report.browser.name = 'Opera';
        }
      }

      if (
        userAgent.indexOf('BB10') >= 0 ||
        userAgent.indexOf('PlayBook') >= 0 ||
        userAgent.indexOf('BlackBerry') >= 0
      ) {
        report.browser.name = 'BlackBerry';
      }

      if (userAgent.indexOf('SmartVideoAgent') >= 0) {
        report.browser.name = 'SmartVideoAgent';
      }

      // extract browser version number from user agent
      match = null;

      switch (report.browser.name) {
        case 'Chrome':
        case 'Chrome for Android':
        case 'WebView on Android':
          match = userAgent.match(/Chrome\/((\d+\.)+\d+)/);
          break;
        case 'Firefox':
        case 'Firefox for Android':
          match = userAgent.match(/Firefox\/((\d+\.)+\d+)/);
          break;
        case 'Firefox for iOS':
          match = userAgent.match(/FxiOS\/((\d+\.)+\d+)/);
          break;
        case 'Edge':
        case 'Internet Explorer':
        case 'IE Mobile':
          if (userAgent.indexOf('Edge') >= 0) {
            match = userAgent.match(/Edge\/((\d+\.)+\d+)/);
          } else if (userAgent.indexOf('rv:11') >= 0) {
            match = userAgent.match(/rv:((\d+\.)+\d+)/);
          } else if (userAgent.indexOf('MSIE') >= 0) {
            match = userAgent.match(/MSIE\ ((\d+\.)+\d+)/);
          }

          break;
        case 'Safari':
        case 'Android Browser':
          match = userAgent.match(/Version\/((\d+\.)+\d+)/);
          break;
        case 'UC Browser for Android':
          match = userAgent.match(/UCBrowser\/((\d+\.)+\d+)/);
          break;
        case 'Opera Mini':
          match = userAgent.match(/Opera Mini\/((\d+\.)+\d+)/);
          break;
        case 'Opera':
          if (userAgent.match(/OPR/)) {
            match = userAgent.match(/OPR\/((\d+\.)+\d+)/);
          } else if (userAgent.match(/Version/)) {
            match = userAgent.match(/Version\/((\d+\.)+\d+)/);
          } else {
            match = userAgent.match(/Opera\/((\d+\.)+\d+)/);
          }
          break;
        case 'BlackBerry':
          match = userAgent.match(/Version\/((\d+\.)+\d+)/);
          break;
        default:
          match = userAgent.match(/\/((\d+\.)+\d+)$/);
          break;
      }

      if (match && match[1]) {
        report.browser.version = match[1];
      }

      // pull in browser window size from the visual viewport
      report.viewport.width =
        window.innerWidth || document.documentElement.clientWidth;
      report.viewport.height =
        window.innerHeight || document.documentElement.clientHeight;

      /*
       * test if Object.defineProperty function is fully supported
       */
      try {
        Object.defineProperty({}, 'x', {});
        definePropertySupported = true;
      } catch (e) {
        definePropertySupported = false;
      }

      /*
       * helper function to safely log warning messages
       */
      function warning(msg) {
        if (window.console) {
          if (console.warn) {
            console.warn(msg);
          } else {
            console.log(msg);
          }
        }
      }

      // deprecate report.browser.size
      if (definePropertySupported) {
        Object.defineProperty(report.browser, 'size', {
          get() {
            warning(
              'browser.size is deprecated; use viewport.width and viewport.height'
            );
            return report.viewport.width + ' x ' + report.viewport.height;
          },
        });
      }

      // pull in raw values for layout viewport
      report.viewport.layout.width = document.documentElement.clientWidth;
      report.viewport.layout.height = document.documentElement.clientHeight;

      // define viewport zoom property
      report.viewport.zoom =
        report.viewport.layout.width / report.viewport.width;

      // are cookies enabled
      // can't trust this value (Microsoft Edge lies)
      // report.cookies = !!navigator.cookieEnabled;

      // truely check if cookies are enabled
      // generate UUID for cookie name
      uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
      uuid = uuid.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;

        return v.toString(16);
      });
      document.cookie = uuid;

      if (document.cookie.indexOf(uuid) >= 0) {
        report.cookies = true;
      } else {
        report.cookies = false;
      }
      // delete temporoary cookie
      document.cookie = uuid + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';

      // check plugins
      ((plugins) => {
        let i: number, l: number, plugin;

        if (plugins) {
          l = plugins.length;

          for (i = 0; i < l; i += 1) {
            plugin = plugins.item(i);
            // console.log(plugin.name);
            // what version of Adobe Flash
            if (plugin.name.indexOf('Flash') >= 0) {
              match = plugin.description.match(/\b((\d+\.)+\d+)\b/);
              if (match && match[1]) {
                report.flash.version = match[1];
              }
            }

            // what version of Java
            if (plugin.name.indexOf('Java') >= 0) {
              match = plugin.description.match(/\b((\d+\.)+\d+)\b/);
              if (match && match[1]) {
                report.java.version = match[1];
              }
            }
          }
        }
      })(navigator.plugins);

      // extract operating system name from user agent
      if (
        userAgent.indexOf('Windows') >= 0 ||
        userAgent.indexOf('SmartVideoAgent') >= 0
      ) {
        if (userAgent.indexOf('Windows Phone') >= 0) {
          report.os.name = 'Windows Phone';
        } else {
          report.os.name = 'Windows';
        }
      }

      if (
        userAgent.indexOf('OS X') >= 0 &&
        userAgent.indexOf('Android') === -1
      ) {
        report.os.name = 'OS X';
        this.mobileOS = 'OS X';
      }

      if (userAgent.indexOf('Linux') >= 0) {
        report.os.name = 'Linux';
      }

      if (userAgent.indexOf('like Mac OS X') >= 0) {
        report.os.name = 'iOS';
        this.mobileOS = 'iOS';
      }

      if (
        (userAgent.indexOf('Android') >= 0 || userAgent.indexOf('Adr') >= 0) &&
        userAgent.indexOf('Windows Phone') === -1
      ) {
        report.os.name = 'Android';
        this.mobileOS = 'Android';
      }

      if (userAgent.indexOf('BB10') >= 0) {
        report.os.name = 'BlackBerry';
      }

      if (userAgent.indexOf('RIM Tablet OS') >= 0) {
        report.os.name = 'BlackBerry Tablet OS';
      }

      if (userAgent.indexOf('BlackBerry') >= 0) {
        report.os.name = 'BlackBerryOS';
      }

      // extract operating system version from user agent
      match = null;

      switch (report.os.name) {
        case 'Windows':
        case 'Windows Phone':
          if (userAgent.indexOf('Win16') >= 0) {
            report.os.version = '3.1.1';
          } else if (userAgent.indexOf('Windows CE') >= 0) {
            report.os.version = 'CE';
          } else if (userAgent.indexOf('Windows 95') >= 0) {
            report.os.version = '95';
          } else if (userAgent.indexOf('Windows 98') >= 0) {
            if (userAgent.indexOf('Windows 98; Win 9x 4.90') >= 0) {
              report.os.version = 'Millennium Edition';
            } else {
              report.os.version = '98';
            }
          } else {
            match = userAgent.match(
              /Win(?:dows)?(?: Phone)?[\ _]?(?:(?:NT|9x)\ )?((?:(\d+\.)*\d+)|XP|ME|CE)\b/
            );

            if (match && match[1]) {
              switch (match[1]) {
                case '6.4':
                  match[1] = '10.0';
                  break;
                case '6.3':
                  match[1] = '8.1';
                  break;
                case '6.2':
                  match[1] = '8';
                  break;
                case '6.1':
                  match[1] = '7';
                  break;
                case '6.0':
                  match[1] = 'Vista';
                  break;
                case '5.2':
                  match[1] = 'Server 2003';
                  break;
                case '5.1':
                  match[1] = 'XP';
                  break;
                case '5.01':
                  match[1] = '2000 SP1';
                  break;
                case '5.0':
                  match[1] = '2000';
                  break;
                case '4.0':
                  match[1] = '4.0';
                  break;
                default:
                  // nothing
                  break;
              }
            }
          }
          break;
        case 'OS X':
          match = userAgent.match(/OS\ X\ ((\d+[._])+\d+)\b/);
          break;
        case 'Linux':
          // linux user agent strings do not usually include the version
          report.os.version = null;
          break;
        case 'iOS':
          match = userAgent.match(/OS\ ((\d+[._])+\d+)\ like\ Mac\ OS\ X/);
          break;
        case 'Android':
          match = userAgent.match(/(?:Android|Adr)\ ((\d+[._])+\d+)/);
          break;
        case 'BlackBerry':
        case 'BlackBerryOS':
          match = userAgent.match(/Version\/((\d+\.)+\d+)/);
          break;
        case 'BlackBerry Tablet OS':
          match = userAgent.match(/RIM Tablet OS ((\d+\.)+\d+)/);
          break;
        default:
          // no good default behavior
          report.os.version = null;
          break;
      }

      if (match && match[1]) {
        // replace underscores in version number with periods
        match[1] = match[1].replace(/_/g, '.');
        report.os.version = match[1];
      }

      // pull in screen info from W3C standard properties
      report.screen.width = screen.width;
      report.screen.height = screen.height;
      report.screen.colors = screen.colorDepth;
      if (window.devicePixelRatio && !isNaN(window.devicePixelRatio)) {
        report.screen.dppx = window.devicePixelRatio;
      } else {
        report.screen.dppx = 1;
      }

      // deprecate report.screen.size
      if (definePropertySupported) {
        Object.defineProperty(report.screen, 'size', {
          get() {
            warning(
              'screen.size is deprecated; use screen.width and screen.height'
            );
            return report.screen.width + ' x ' + report.screen.height;
          },
        });
      }

      // deprecate report.screen.resolution
      if (definePropertySupported) {
        Object.defineProperty(report.screen, 'resolution', {
          get() {
            warning(
              'screen.resolution is deprecated; multiply screen.width and screen.height by screen.dppx'
            );
            return (
              report.screen.dppx * report.screen.width +
              ' x ' +
              report.screen.dppx * report.screen.height
            );
          },
        });
      }

      // are web sockets supported
      report.websockets = !!window['WebSocket'];

      // preferred language(s) for displaying pages
      report.lang = navigator.languages || navigator.language;

      // local date, time, and time zone
      report.timestamp = new Date().toString();

      return report;
    }

    browserSupported() {
      let isOsSupported = false;
      let isBrowserSupported = false;
      let versionOsArray;
      let majorOsVersion;
      let minorOsVersion;
      let revOsVersion;
      let versionBrowserArray;
      let majorBrowserVersion;
      let minorBrowserVersion;
      let revBrowserVersion;
      const report = this.browserReport;

      if (report.os.version && report.os.version.indexOf('.') === -1) {
        majorOsVersion = report.os.version;
      }
      if (report.os.version && report.os.version.indexOf('.') > 0) {
        versionOsArray = report.os.version.split('.');
        majorOsVersion = versionOsArray[0];
        minorOsVersion = versionOsArray[1] ? versionOsArray[1] : 0;
        revOsVersion = versionOsArray[2] ? versionOsArray[2] : 0;
      }
      if (report.browser.version && report.browser.version.indexOf('.') > 0) {
        versionBrowserArray = report.browser.version.split('.');
        majorBrowserVersion = versionBrowserArray[0];
        minorBrowserVersion = versionBrowserArray[1]
          ? versionBrowserArray[1]
          : 0;
        revBrowserVersion = versionBrowserArray[2] ? versionBrowserArray[2] : 0;
      }

      switch (report.os.name) {
        case 'Windows':
          switch (majorOsVersion) {
            case '7':
              isOsSupported = true;
              this.isWindows = true;
              break;
            case '8':
              isOsSupported = true;
              this.isWindows = true;
              break;
            case '10':
              isOsSupported = true;
              this.isWindows = true;
              break;
            default:
              this.isWindows = true;
              break;
          }
          break;
        case 'OS X':
          const osxVersion = report.os.version.replace('.', '');
          if (osxVersion >= 108) {
            isOsSupported = true;
          }
          this.isOSX = true;
          break;
        case 'iOS':
          isOsSupported = true;
          this.isIOS = true;
          /* if (report.browser.name === 'Safari') {
            isOsSupported = true;
          } else {
            isOsSupported = false;
          } */

          break;
        case 'Android':
          isOsSupported = true;
          break;
        default:
          break;
      }

      switch (report.browser.name) {
        case 'Internet Explorer':
          if (majorBrowserVersion === '11') {
            isBrowserSupported = false;
            this.isIE = true;
          }
          break;
        case 'Safari':
          /* if (
            majorBrowserVersion === 9 &&
            minorBrowserVersion === 0 &&
            revBrowserVersion >= 3
          ) {
            isBrowserSupported = true;
          }
          if (majorBrowserVersion === 9 && minorBrowserVersion >= 1) {
            isBrowserSupported = true;
          }
          if (majorBrowserVersion > 9) {
            isBrowserSupported = true;
          }
          if (majorBrowserVersion >= 11) {
            this.isWebRTCCapable = true;
          } */
          if (this.isOSX) {
            isBrowserSupported = true;
          } else if (this.isIOS) {
            isBrowserSupported = true;
          }
          this.isWebRTCCapable = false;
          this.isSafari = true;
          break;
        case 'Firefox for Android':
          this.isFireFox = true;
          isBrowserSupported = true;
          this.isWebRTCCapable = true;
          break;
        case 'Firefox':
          this.isFireFox = true;
          if (this.useWebRTC) {
            if (majorBrowserVersion >= 53 && isOsSupported) {
              isBrowserSupported = true;
              this.isWebRTCCapable = true;
            }
          } else {
            isBrowserSupported = false;
            this.isWebRTCCapable = false;
          }

          break;
        case 'Firefox for iOS':
          this.isFireFox = true;
          isBrowserSupported = true;
          this.isWebRTCCapable = false;
          break;
        case 'Chrome for Android':
        case 'Chrome':
          this.isChrome = true;
          if (majorBrowserVersion >= 48 && isOsSupported) {
            isBrowserSupported = true;
            this.isWebRTCCapable = true;
          }
          break;
        case 'Chrome for iOS':
          this.isChrome = true;
          if (majorBrowserVersion >= 48 && isOsSupported) {
            isBrowserSupported = true;
            this.isWebRTCCapable = false;
          }
          break;
        case 'Edge':
          this.isEdge = true;
          /*
          Edge does not fully support WebRTC. Microsoft has said they will not be
          implementing the full WebRTC 1.0 standard. They only fully support ORTC.
          Until Microsoft fully supports WebRTC, or Vidyo supports ORTC,
          it's likely Edge may never work with Vidyo
          */
          /*if (this.useWebRTC) {
              if (majorBrowserVersion >= 15 && minorBrowserVersion >=15063) {
                  isBrowserSupported = true;
                  this.isWebRTCCapable = true;
              }
          }*/
          break;
        case 'Opera':
          if (this.useWebRTC && isOsSupported) {
            isBrowserSupported = true;
            this.isOpera = true;
            this.isWebRTCCapable = true;
          }
          break;
        default:
          break;
      }

      this.isOsSupported = isOsSupported;
      this.isBrowserSupported = isBrowserSupported;
      if (isOsSupported) {
        this.supportedOsName = report.os.name;
      }
      console.log(JSON.stringify(this.browserReport));
    }

    detectExtension(extensionId: string) {
      console.log('Now checking Vidyo Chrome extension');
      // Only check for VidyoClientForWeb extension if Windows
      if (!this.useWebRTC && this.isWindows) {
        /* Vidyo exposes an icon for the purpose of detection with their NMS extension.
                This is a hack. This hack also fails inexplicably at times.
                As a backup, receiveVidyoAppEvent() will also set isChromeExtensionInstalled when the icon detection fails
                but we are receiving messages from the extension.
                The combination of the two seems to ensure the extension is correctly detected in all circumstances
                */
        this.img.src =
          'chrome-extension://' + extensionId + '/icons/128x128.png';
        if (this.img.naturalWidth > 0) {
          this.isChromeExtensionInstalled = true;
          console.log('Chrome NMS extension found');
          return true;
        } else {
          console.log(
            'Chrome NMS Extension image src not found,' +
              'may be due to Chrome bug, will send message to extension and look for response'
          );
          return false;
        }
      }
      if (!this.useWebRTC && !this.isWindows) {
        console.log(
          'Ignoring extension check since WebRTC not available and not using Windows'
        );
        return false;
      }
      if (this.useWebRTC) {
        /* Vidyo learned their lessons when creating the the WebRTC Screen Share extension.
                The extension inserts an element into the web page, and we check for that element.
                So if the element exists, it means the extension has been installed/loaded.
                That's what they should have done with the NMS extension but didn't.
                */
        if (document.getElementById('vidyowebrtcscreenshare_is_installed')) {
          this.isChromeExtensionInstalled = true;
          // Since this is WebRTC, and the screen share extension is all that is needed, also mark isInstalled as true
          this.isInstalled = true;
          console.log('Chrome Sharing Extension found');
          return true;
        } else {
          console.log('Chrome Sharing Extension not found');
          return false;
        }
      }
    }

    connectVidyoClientForWeb(callback?: Function) {
      const connectVersionRequest = {
        type: 'RequestConnectVersion',
        version: this.chromeNativeMessagingHostVersion,
      };
      /*
            Don't care about the response(for now), but have to call RequestConnectVersion
            before Extension will connect to Native Messaging Host.
            So we kick this off as soon as we can when class inits.
            */
      this.sendVidyoAppRequest(connectVersionRequest, () => {
        this.doCallback(callback);
      });
    }

    detectVidyoClientForWeb(callback?: Function) {
      console.log('Now checking for VidyoClientForWeb (Chrome NMS)');
      const getVersionRequest = {
        type: 'RequestGetVersion',
      };

      this.connectVidyoClientForWeb();

      this.sendVidyoAppRequest(getVersionRequest, (getVersionResponse) => {
        if (
          getVersionResponse.version === this.chromeNativeMessagingHostVersion
        ) {
          this.isChromeNativeMessagingHostInstalled = true;
          this.isInstalled = true;
          console.log('Chrome NMS found');
          console.log('Removing the message listener');
          window.removeEventListener('message', this.receiveListener, false);
          if (callback) {
            this.doCallback(callback);
          }
        }
      });
    }

    detectCamera() {
      this.isCameraReady = false;
      const that = this;
      navigator.mediaDevices.enumerateDevices().then(function (sourceInfos) {
        for (let index = 0; index < sourceInfos.length; index++) {
          const deviceInfo = sourceInfos[index];
          if (deviceInfo.kind === 'videoinput') {
            console.log('Camera found: ' + deviceInfo.deviceId);
            that.isCameraReady = true;
            if (that.fireFoxCameraDetectionFunction) {
              clearInterval(that.fireFoxCameraDetectionFunction);
            }
          }
        }
        if (!that.isCameraReady) {
          // console.log("Camera not found");
        }
      });
    }

    detectWebRTCAvailable(): Promise<void> {
      /*Check if we can successfully connect to the WebRTC session manager
            This is just an initial check, if this fails then WebRTC is definitely
            not available*/
      /*All WebRTC capable browsers support Promises, so safe to use it here*/
      const that = this;
      let data = null;
      console.log(
        'Contacting WebRTCSessionManager to determine if WebRTC available'
      );
      return new Promise<void>(function (resolve, reject) {
        if (
          that.config.WebRTCSessionManager &&
          that.config.WebRTCSessionManager !== ''
        ) {
          let instancePath = '/zincadmin/service/getinstance.htm';
          if (that.config.UseNeoOrAnyConnect) {
            instancePath = '/webrtc/service/getinstance.htm';
          }
          const url =
            window.location.protocol +
            '//' +
            that.config.WebRTCSessionManager +
            instancePath;
          Ajax.ajaxSendRequest(
            url,
            'GET',
            null,
            function (e) {
              const xhr = <XMLHttpRequest>e.target;
              if (xhr.responseType === 'json') {
                data = xhr.response;
              } else {
                data = JSON.parse(xhr.responseText);
              }
              if (data !== null) {
                if (data.ip) {
                  resolve(data.ip);
                } else {
                  reject(
                    Error(
                      'detectWebRTCAvailable: Did not get IP field from response'
                    )
                  );
                }
              } else {
                // If we get no response at all, we'll get here
                reject(
                  Error(
                    'detectWebRTCAvailable: Did not get IP field from response'
                  )
                );
              }
            },
            'json'
          );
        } else {
          console.log(
            'detectWebRTCAvailable: WebRTCSessionManager value is empty'
          );
          reject(
            Error('detectWebRTCAvailable: WebRTCSessionManager value is empty')
          );
        }
      });
    }

    npApiPluginInstalled() {
      navigator.plugins.refresh(false);
      let vidyoPluginMimeType = 'application/x-vidyoweb-';
      vidyoPluginMimeType += this.pluginVersion;
      const mimes = navigator.mimeTypes;

      /*We can't tell from plugin if camera is ready, so have
            to assume that it is.
            Mark the camera as ready so we bypass camera check when NPAPI,
            so we can then move to install modals, if necessary*/

      this.isCameraReady = true;

      /* Check NPAPI
            NOTE: Every damn browser has different implementation of MimeType Arrays,
            so have to check properties on plugin to determine how to interrogate it.
            The only browser we support that allows NPAPI still is Safari */
      if (!this.isIE) {
        for (let i = 0; i < mimes.length; i++) {
          // if (mimes[i].type === pluginMimeType) {
          const plugin = <CustomPluginType>mimes[i];
          if (plugin.item && plugin.item(0).type === vidyoPluginMimeType) {
            console.log('NPAPI plugin found');
            return true;
          }
          if (plugin.type && plugin.type === vidyoPluginMimeType) {
            console.log('NPAPI plugin found');
            return true;
          }
        }
      }
      // Check IE
      if (this.isIE) {
        try {
          const control = new ActiveXObject(
            'VidyoInc.VidyoWeb_' + this.pluginVersion
          );
          if (control) {
            console.log('ActiveX plugin found');
            return true;
          }
        } catch (e) {
          console.log(
            'Tried to load ActiveX control, but failed: ' + e.message
          );
        }
      }

      return false;
    }

    checkVidyoComponents(callback?: Function) {
      console.log('Now checking Vidyo components');
      if (window.localStorage.getItem('useNeoVidyo')) {
        console.log(
          'useNeo flag found in local storage, setting isInstalled to true'
        );
        this.isInstalled = true;
        return;
      }

      if (this.isSmartVideoAgent) {
        console.log('isSmartVideoAgent flag set, setting isInstalled to true');
        this.isInstalled = true;
        return;
      }

      if (this.isSafari && this.useWebRTC) {
        if (this.isWebRTCAvailable) {
          console.log('Using WebRTC and Safari, setting isInstalled to true');
          this.isInstalled = true;
          return;
        }
      }

      if (!this.isChrome) {
        // Not using WebRTC. Firefox not longer supports NPAPI, so exclude it (Only IE/Safari still support NPAPI)
        if (!this.useWebRTC && !this.isFireFox) {
          console.log('Not using WebRTC and not Chrome, checking NPAPI');
          this.isInstalled = this.npApiPluginInstalled();
          return;
        } else if (this.isFireFox || this.isEdge) {
          /*
                    Using WebRTC but not Chrome
                    Edge does not fully implement WebRTC 1.0. Currently only fully supports ORTC.
                    There is a WebRTC shim/polyfill for Edge, but it would greatly increase the size/complexity of WebRTCAdapter.ts
                    Opera uses Chromium, so works largely the same as Chrome,
                    but can't use Chrome extensions directly (although programmatically they work exactly the same)
                    So for Opera, can't do screen share until an Opera Vidyo extension is created
                    (which would be simple for Vidyo or even Adapt to do)
                    */
          if (this.isWebRTCAvailable) {
            console.log(
              'Using WebRTC and Firefox/Edge, setting isInstalled to true'
            );
            this.isInstalled = true;
          } else {
            /*
                        If we are configured for WebRTC, but it's not available,
                        there is no fallback avaialble for Firefox/Edge. In that case,
                        mark browser as unsupported
                        */
            this.isInstalled = false;
            this.isBrowserSupported = false;
          }
          return;
        } else {
          // Using WebRTC, but not supported browser (Chrome/Firefox), so fall back to plugin
          console.log('Using WebRTC and non-WebRTC browser, checking NPAPI');
          this.isInstalled = this.npApiPluginInstalled();
        }
        return;
      }
      if (this.isChrome || this.isOpera) {
        // setTimeout(() => { this.detectExtension(this.extensionId) }, 2000);
        console.log('Now checking for Chrome extension: ' + this.extensionId);
        this.detectExtension(this.extensionId);
        // Only check for Chrome NMS (plugin) if not using WebRTC and if Windows
        if (!this.useWebRTC && this.isWindows) {
          console.log(
            'Not using WebRTC and is Chrome on Windows, now checking for Chrome NMS'
          );
          // setTimeout(() => { this.detectVidyoClientForWeb() }, 2000);
          this.detectVidyoClientForWeb();
        }
        // If not using WebRTC and not Windows, then we can't support Chrome
        if (!this.useWebRTC && !this.isWindows) {
          this.isInstalled = false;
          this.isBrowserSupported = false;
        }
        return;
      }
    }

    checkInstallProgress(
      installComponent: string,
      nextInstallFunction: Function,
      callback: Function
    ) {
      const timeoutcallback = () => {
        this.checkInstallProgress(
          installComponent,
          nextInstallFunction,
          callback
        );
      };
      const response = {
        isInstalled: false,
        nextInstallFunction: nextInstallFunction,
        installComponent: installComponent,
        hasError: false,
        errorMsg: '',
      };

      /*
            Save install information, because we may need to refresh page (for Chrome extension)
            and in case something happens before install(s) complete
            */
      window.localStorage.setItem('installComponent', installComponent);

      switch (installComponent) {
        case 'Chrome Extension':
          const chromeExtensionPresent = this.detectExtension(this.extensionId);
          if (chromeExtensionPresent) {
            this.isChromeExtensionInstalled = true;
            response.isInstalled = true;
            response.hasError = false;
          }
          // Only check for Chrome NMS (plugin) if not using WebRTC
          if (!this.useWebRTC) {
            this.detectVidyoClientForWeb();
          }

          if (this.isChromeExtensionInstalled) {
            response.isInstalled = true;
            response.hasError = false;
          }
          break;
        case 'Chrome Plugin':
          if (!this.isChromeNativeMessagingHostInstalled) {
            this.detectVidyoClientForWeb();
          }
          if (this.isChromeNativeMessagingHostInstalled) {
            this.isChromeNativeMessagingHostInstalled = true;
            response.isInstalled = true;
            response.hasError = false;
            console.log('Removing the message listener');
            window.removeEventListener('message', this.receiveListener);
          }
          break;
        case 'NPAPI Plugin':
          const npApiPluginPresent = this.npApiPluginInstalled();
          if (npApiPluginPresent) {
            this.isInstalled = true;
            response.isInstalled = true;
            response.hasError = false;
          }
          break;
        case 'Neo':
          if (window.localStorage.getItem('useNeoVidyo') === 'true') {
            this.isInstalled = true;
            response.isInstalled = true;
            response.hasError = false;
          }
          break;
        default:
          response.errorMsg = `Install component is not recognized: ${installComponent}`;
      }
      if (response.isInstalled || response.hasError) {
        this.doCallback(callback(response));
        return;
      } else {
        console.log('Starting install timer');
        this.installTimer = setTimeout(timeoutcallback.bind(this), 3000);
      }
    }

    sendVidyoAppRequest(obj: any, callback: Function) {
      obj._id = Math.floor(Math.random() * 1000000);
      this.callbacks[obj._id] = callback;
      clearTimeout(this.timeouts[obj._id]);

      this.timeouts[obj._id] = setTimeout(() => {
        console.log('timeout:', obj.type);
        obj.error = 'connection timeout';
        if (this.callbacks[obj._id] && this.timeouts[obj._id]) {
          this.callbacks[obj._id](obj);
        }
      }, 2000);

      obj._direction = 1;

      console.log('snd to ext:', JSON.stringify(obj));
      window.postMessage(obj, '*');
    }

    receiveVidyoAppEvent(event: any) {
      const message = event.data;
      if (message.type === 'log') {
        console.log('rcv from ext:', JSON.stringify(message));
      } else {
        console.log('rcv from ext:', JSON.stringify(message));
      }

      if (message._direction === 2) {
        /*
                We are setting isChromeExtensionInstalled to true here because sometimes detectExtension fails inexplicably, yet we get messages
                So when this occurs, setting isChromeExtensionInstalled here ensures the state of extension install is correct.
                */
        this.isChromeExtensionInstalled = true;
        console.log(
          'Received message from Chrome extension, this verifies extension is installed'
        );
        console.log(
          'Chrome Install boolean set to: ' + this.isChromeExtensionInstalled
        );
        if (message.type) {
          if (message.type.indexOf('OUT_EVENT') === -1) {
            if (this.callbacks[message._id] && this.timeouts[message._id]) {
              clearTimeout(this.timeouts[message._id]);
              delete this.timeouts[message._id];
              this.callbacks[message._id](message);
              delete this.callbacks[message._id];
            }
            return;
          }
        }
      }
    }
  }
}
